import { Navigate } from "react-router-dom";
import { useFeatureToggle } from "../../hooks/useFeatureToggle";
import { Spinner } from "../../shared";

/**
 * FeatureGuard - Conditional rendering based on feature toggle state
 *
 * Wraps components/routes to control access based on feature state:
 * - "on": Renders children for all users
 * - "disabled": Renders children only for users with VIEW_DISABLED_FEATURES permission
 * - "off": Redirects to fallback or shows nothing
 *
 * @param {Object} props
 * @param {string} props.feature - Feature key to check
 * @param {React.ReactNode} props.children - Content to render if access is granted
 * @param {string} props.redirectTo - Where to redirect if access is denied (default: "/feature-disabled")
 * @param {React.ReactNode} props.fallback - Alternative content if access is denied (instead of redirect)
 * @param {boolean} props.showSpinner - Show spinner while loading (default: true)
 */
const FeatureGuard = ({
  feature,
  children,
  redirectTo = "/feature-disabled",
  fallback = null,
  showSpinner = true,
}) => {
  const { canAccess, isLoading } = useFeatureToggle(feature);

  // Show loading spinner while checking feature state
  if (isLoading && showSpinner) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  // If user doesn't have access
  if (!canAccess) {
    // Use custom fallback if provided
    if (fallback) {
      return fallback;
    }

    // Otherwise redirect to feature disabled page
    return <Navigate to={redirectTo} replace />;
  }

  // User has access - render children
  return <>{children}</>;
};

export default FeatureGuard;
